/**
 * Page 1 — Apply for Reimbursement (Certifications).
 *
 * ── It is a page, not a form ──────────────────────────────────────────────────────────────
 *
 * Applying is one thing this page does. The rest of it is the employee's OWN claims: what each
 * one is for, whether the exam was passed, where it has got to with its approvers, and — for a
 * failed certification — how much of the three-month retake window is left, counted live.
 *
 * That last part is why this cannot be a link to a form. An employee who failed needs to know
 * how long they have, and the page where they applied is the only place they would think to look.
 *
 * ── Reading from Certification_Reimbursements, not Expenses ───────────────────────────────
 *
 * A certification claim is its own record in its own table. Nothing on this page touches the
 * Expenses module, and a claim's two approval verdicts live on the claim itself rather than
 * being inferred from a shared expense status.
 *
 * ── Two statuses on every row, deliberately kept apart ────────────────────────────────────
 *
 *   Result    Passed / Failed             did the employee pass the exam
 *   Approval  Pending Manager / Approved  what the approvers decided about paying
 *
 * They are routinely confused and the confusion matters: a failed exam is not a rejected claim,
 * and a claim can be approved for an exam that was failed. Different columns, different shapes.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Certification, CertificationResult } from '../../types/certification';
import {
  CertificationReimbursement,
  ReimbursementApprovalStatus,
  ReimbursementStatus,
} from '../../types/certificationReimbursement';
import { CertificationReimbursementService } from '../../services/certificationReimbursementService';
import {
  CertificationService,
  CertificationsNotDeployedError,
  describeCertificationError,
} from '../../services/certificationService';
import { getCertificateUrl } from '../../services/certificationDocuments';
import { canonicalizeEmail } from '../../services/identity';
import AddCertificationModal from './AddCertificationModal';
import ApplyReimbursementModal from './ApplyReimbursementModal';
import ApprovalStages from './ApprovalStages';
import CertificateViewerModal, { ViewingCertificate } from './CertificateViewerModal';
import RetakeCountdown from './RetakeCountdown';
import RetakeCell from './RetakeCell';
import './Certifications.css';

const formatDate = (iso?: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  if (!match) return '—';
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${match[3]}-${MONTHS[Number(match[2]) - 1]}-${match[1]}`;
};

const money = (amount: number, currency: string) =>
  `${currency === 'INR' ? '₹' : `${currency} `}${Number(amount || 0).toLocaleString()}`;

const statusTone = (status: ReimbursementStatus): string => {
  /* REIMBURSED replaced APPROVED as the terminal state: the workflow now ends when finance has
     paid, not when the certification admin has said yes. Everything before it is still waiting. */
  if (status === ReimbursementStatus.REIMBURSED) return 'ok';
  if (status === ReimbursementStatus.REJECTED) return 'no';
  return 'wait';
};

/**
 * One approver's verdict, in the words an applicant needs.
 *
 * `reachedThisStage` is why this is not a plain lookup: "Pending" on a stage the claim has not
 * reached would tell the employee somebody is sitting on it, when in fact it has not been passed
 * to them yet.
 */
const verdictLabel = (
  verdict: ReimbursementApprovalStatus,
  reachedThisStage: boolean
): string => {
  if (verdict === ReimbursementApprovalStatus.APPROVED) return 'Approved';
  if (verdict === ReimbursementApprovalStatus.REJECTED) return 'Rejected';
  return reachedThisStage ? 'Pending' : 'Not reached';
};

const CertificationReimbursementPage: React.FC = () => {
  const { user } = useAuth();
  const myEmail = canonicalizeEmail(user?.email);

  const [claims, setClaims] = useState<CertificationReimbursement[]>([]);
  const [certsById, setCertsById] = useState<Map<string, Certification>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  /* The certificate currently on screen, or null. See CertificateViewerModal. */
  const [viewing, setViewing] = useState<ViewingCertificate | null>(null);
  /* Distinguished from `error` because it changes what the page may CLAIM, not just what it
     shows. A read that failed for any other reason is still a page whose Apply button works. */
  const [notDeployed, setNotDeployed] = useState(false);
  const [applying, setApplying] = useState(false);
  const [retakeFor, setRetakeFor] = useState<Certification | null>(null);
  /** Which claim is expanded. One at a time — the detail is tall. */
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setNotDeployed(false);
    try {
      /*
       * Both reads go through the byEmployee index, so the browser is never handed somebody
       * else's claims or certifications to filter out — the filtering is the query.
       */
      const [mine, certifications] = await Promise.all([
        CertificationReimbursementService.listForEmployee(myEmail),
        CertificationService.listForEmployee(myEmail),
      ]);
      setClaims(mine);
      setCertsById(new Map(certifications.map((c) => [c.id, c])));
    } catch (err: any) {
      setError(describeCertificationError(err, 'Could not load your certification reimbursements.'));
      setNotDeployed(err instanceof CertificationsNotDeployedError);
    } finally {
      setLoading(false);
    }
  }, [myEmail]);

  useEffect(() => {
    load();
  }, [load]);

  const openDocument = async (claim: CertificationReimbursement) => {
    try {
      const url = await getCertificateUrl(
        claim.documentKey,
        claim.documentName || claim.certificationName
      );
      /* In a dialog over the queue — see CertificateViewerModal. */
      setViewing({
        url,
        title: claim.certificationName,
        documentName: claim.documentName || undefined,
      });
    } catch (err: any) {
      setError(err?.message || 'The document could not be opened.');
    }
  };

  return (
    <div className="certs">
      <div className="certs__head">
        <div>
          <h1>Apply for Reimbursement</h1>
          <p>
            Claim back the cost of a certification. Applications go to your manager and then to
            the certification admin. A failed exam can still be claimed — the exam result and the
            decision on your claim are separate things.
          </p>
        </div>
        {/* Disabled when there is no backend to apply against. It used to stay live, so the
            only way to discover the module was undeployed was to fill the whole form in and
            have it rejected on submit — the form cannot succeed, and offering it says it can. */}
        <button
          type="button"
          className="certs__btn certs__btn--primary"
          onClick={() => setApplying(true)}
          disabled={notDeployed}
          title={notDeployed ? 'The certifications module is not deployed in this environment.' : undefined}
        >
          + Apply for Reimbursement
        </button>
      </div>

      {error && <div className="certs__notice certs__notice--error" role="alert">{error}</div>}

      {/* The heading goes with its content. Suppressing only the empty state left this standing
          over an empty page, which reads as a section that failed to load rather than one that
          was never reachable — the notice above has already said everything true about it.
          Kept when there ARE claims to show, so an error beside stale rows still labels them. */}
      {!(error && claims.length === 0) && (
        <h2 className="certs__section">My Submitted Certification Reimbursements</h2>
      )}

      {/*
        * `error &&` guards the empty state, and that guard is the point.
        *
        * A read that FAILED leaves `claims` at [], which is indistinguishable from a read that
        * succeeded and found nothing — so the page used to render the notice and "You have not
        * applied for any certification reimbursements" together, asserting both that the API is
        * unavailable and that it had checked and found none. The second is a confident, wrong
        * answer, and it sent people to All Certifications, which is equally undeployed.
        *
        * An unknown list is not an empty list. When the read failed the notice speaks alone.
        */}
      {loading && claims.length === 0 ? (
        <div className="certs__skeleton" />
      ) : error ? null : claims.length === 0 ? (
        <div className="certs__empty">
          <h3>You have not applied for any certification reimbursements.</h3>
          <p>
            Use the button above to claim one. To record a certification you already hold without
            claiming anything, use All Certifications instead.
          </p>
        </div>
      ) : (
        <div className="certs__table-wrap">
          <table className="certs__table">
            <thead>
              <tr>
                <th>Certification</th>
                <th>Result</th>
                <th>Submitted</th>
                <th>Amount</th>
                <th>Approval</th>
                <th>Retake</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {claims.map((claim) => {
                const isFailed = claim.result === CertificationResult.FAILED;
                const expanded = openId === claim.id;
                const certification = certsById.get(claim.certificationId);
                const managerApproved =
                  claim.managerApprovalStatus === ReimbursementApprovalStatus.APPROVED;
                return (
                  <React.Fragment key={claim.id}>
                    <tr>
                      <td>
                        <span className="certs__name">{claim.certificationName}</span>
                        <span className="certs__who-id">{claim.provider}</span>
                      </td>
                      <td>
                        <span
                          className={`certs__pill certs__pill--${
                            claim.result === CertificationResult.PASSED ? 'passed' : 'failed'
                          }`}
                        >
                          {claim.result}
                        </span>
                      </td>
                      <td>{formatDate(String(claim.createdAt).slice(0, 10))}</td>
                      <td>{money(claim.reimbursementAmount, claim.currency)}</td>
                      <td>
                        {/* A different shape from the result pill on purpose — see the header. */}
                        <span className={`certs__status certs__status--${statusTone(claim.status)}`}>
                          {claim.status}
                        </span>
                      </td>
                      <td>
                        <RetakeCell deadline={isFailed ? claim.retakeDeadline : undefined} />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="certs__link"
                          aria-expanded={expanded}
                          onClick={() => setOpenId(expanded ? null : claim.id)}
                        >
                          {expanded ? 'Hide' : 'View'}
                        </button>
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="cert-detail-row">
                        <td colSpan={7}>
                          <div className="cert-detail">
                            <ApprovalStages claim={claim} />

                            <div className="cert-detail__grid">
                              <div><span>Certification</span><strong>{claim.certificationName}</strong></div>
                              <div><span>Provider</span><strong>{claim.provider}</strong></div>
                              <div><span>Exam / written date</span><strong>{formatDate(claim.examDate)}</strong></div>
                              <div><span>Result date</span><strong>{formatDate(claim.resultDate)}</strong></div>
                              <div><span>Result</span><strong>{claim.result}</strong></div>
                              <div>
                                <span>Reimbursement amount</span>
                                <strong>{money(claim.reimbursementAmount, claim.currency)}</strong>
                              </div>
                              <div>
                                <span>Manager approval</span>
                                <strong>{verdictLabel(claim.managerApprovalStatus, true)}</strong>
                              </div>
                              <div>
                                <span>Admin approval</span>
                                <strong>
                                  {verdictLabel(claim.adminApprovalStatus, managerApproved)}
                                </strong>
                              </div>
                              <div><span>Overall status</span><strong>{claim.status}</strong></div>
                              <div>
                                <span>Document</span>
                                <strong>
                                  {claim.documentKey ? (
                                    <button
                                      type="button"
                                      className="certs__link"
                                      onClick={() => openDocument(claim)}
                                    >
                                      View
                                    </button>
                                  ) : (
                                    '—'
                                  )}
                                </strong>
                              </div>
                            </div>

                            {/*
                              * The ONLY retake UI on this page.
                              *
                              * There used to be a second "Retake required" section below the
                              * table repeating all of this — the same dates, the same countdown,
                              * the same button — for every failed claim. Two copies of one
                              * control on one screen is not emphasis, it is a question about
                              * which one is real. This is the copy that sits with the claim it
                              * belongs to. All Certifications carries its own entry point for a
                              * certification with no claim.
                              */}
                            {isFailed && (
                              <div className="cert-detail__retake">
                                <div className="cert-retake__dates">
                                  <div>
                                    <span>Result Date</span>
                                    <strong>{formatDate(claim.resultDate)}</strong>
                                  </div>
                                  <div>
                                    <span>Retake Deadline</span>
                                    <strong>{formatDate(claim.retakeDeadline)}</strong>
                                  </div>
                                </div>
                                <RetakeCountdown deadline={claim.retakeDeadline} />
                                {certification ? (
                                  <button
                                    type="button"
                                    className="certs__btn certs__btn--primary"
                                    onClick={() => setRetakeFor(certification)}
                                  >
                                    I have retaken this exam
                                  </button>
                                ) : (
                                  /* The claim survives without its certification, but a retake
                                     cannot be recorded against a record that is not loaded. */
                                  <p className="cert-form__hint">
                                    The certification record for this claim could not be loaded, so
                                    a retake cannot be recorded here. Use All Certifications.
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {applying && (
        <ApplyReimbursementModal
          onClose={() => setApplying(false)}
          onSubmitted={() => {
            setApplying(false);
            load();
          }}
        />
      )}

      {retakeFor && (
        <AddCertificationModal
          retakeOf={retakeFor}
          onClose={() => setRetakeFor(null)}
          onSaved={() => {
            setRetakeFor(null);
            load();
          }}
        />
      )}

      {viewing && <CertificateViewerModal {...viewing} onClose={() => setViewing(null)} />}
    </div>
  );
};

export default CertificationReimbursementPage;
